import React from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { StatusContextValue, useContextStatus } from '../common/ContextStatus';
import { NearbyWifiNetwork } from '../TelematicsDevice/DeviceTelemetryApi';
import { WifiScanApi } from './WifiScanApi';

type WifiScanContextValue = StatusContextValue & {
  networks: NearbyWifiNetwork[];
  networksUpdateTime?: string;
  loadLastScan: (deviceId: string) => Promise<void>;
};

const EMPTY_WIFI_SCAN_CONTEXT: WifiScanContextValue = {
  networks: [],
  networksUpdateTime: undefined,
  loadLastScan: () => Promise.resolve(),
  contextStatus: {
    loading: false,
    saving: false,
    hasContent: false,
  },
  setContextStatus: () => undefined,
};

const WifiScanContext = React.createContext<WifiScanContextValue | undefined>(
  undefined
);

type WifiScanProviderProps = {
  children: React.ReactNode;
};

const WifiScanProvider = (props: WifiScanProviderProps) => {
  const { authState } = useOktaAuth();
  const [networks, setNetworks] = React.useState<NearbyWifiNetwork[]>([]);
  const [networksUpdateTime, setNetworksUpdateTime] = React.useState<string | undefined>();
  const [contextStatus, setContextStatus] = React.useState({
    loading: false,
    saving: false,
    hasContent: false
  });

  const status = useContextStatus({ contextStatus, setContextStatus });

  const loadLastScan = (deviceId: string): Promise<void> => {
    status.loadingStarted();

    return WifiScanApi.getLastScan(authState?.accessToken, deviceId)
      .then(scan => {
        setNetworks(scan.nearbyWifiNetworks);
        setNetworksUpdateTime(scan.nearbyWifiNetworksUpdatedTime);
        status.succeed();
      })
      .catch(err => {
        setNetworks([]);
        setNetworksUpdateTime(undefined);
        status.failed(err.message, 'Could not load nearby Wi-Fi networks');
      });
  };

  const value: WifiScanContextValue = {
    networks,
    networksUpdateTime,
    loadLastScan,
    contextStatus,
    setContextStatus,
  };

  return <WifiScanContext.Provider value={value} {...props} />;
};

const useWifiScanContext = () => {
  return React.useContext(WifiScanContext) || EMPTY_WIFI_SCAN_CONTEXT;
};

export { WifiScanProvider, useWifiScanContext };
